import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaDollarSign, FaFileInvoice } from "react-icons/fa";
import Select from "react-select";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, LineChart, CartesianGrid, Line, YAxis, XAxis} from "recharts";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function UserDashboard1() {
  const [dashboardData, setDashboardData] = useState({ stockValuation: 0 });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [stockValuationData, setStockValuationData] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [overdueInvoiceCount, setOverdueInvoiceCount] = useState(0);
  const [monthlyInvoiceData, setMonthlyInvoiceData] = useState([]);
  const [monthlySettlementData, setMonthlySettlementData] = useState([]);

  useEffect(() => {
    if (selectedLocation) {
      fetchMonthlyInvoicesAndSettlements();
    } 
  }, [selectedLocation]);

  useEffect(() => {
    fetchOverdueInvoices();
  })

  const fetchMonthlyInvoicesAndSettlements = async () => {
    const token = localStorage.getItem("token");
    try {
      const [invoiceResponse, settlementResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/invoices/monthlyAmounts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { selectedLocation: selectedLocation.value },
        }),
        axios.get(`${API_BASE_URL}/settlements/getMonthlySettledAmounts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { selectedLocation: selectedLocation.value },
        }),
      ]);

      const invoiceData = invoiceResponse.data.data;       
      const settlementData = settlementResponse.data.data;

      // Merge datasets by month
      const mergedData = invoiceData.map(invoice => {
        const settlement = settlementData.find(s => s.month === invoice.month);
        return {
          ...invoice,
          settledAmount: settlement ? settlement.settledAmount : 0,
          actualSettledAmount: invoice.amount - invoice.balance,
          balanceAmount: invoice.balance ?? (invoice.amount - (settlement?.settledAmount || 0)),
        };
      });

      setMonthlyInvoiceData(mergedData); // contains both amount and settledAmount

    } catch (error) {
      console.error("Error fetching monthly invoices and settlements:", error);
      toast.error("Failed to fetch monthly invoices and settlements.");
    }
  };

  const fetchOverdueInvoices = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_BASE_URL}/invoices/getoverdueInvoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setOverdueInvoiceCount(response.data.count);
    } catch (error) {
      console.error("Error fetching overdue invoices:", error);
    }
  }

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedLocation) fetchStockValuation();
  }, [selectedLocation]);

  useEffect(() => {
    if (selectedLocation) fetchStockValuationForCard();
  }, [selectedLocation]);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token missing. Please log in.");
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/users/specific`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const userData = response.data;
      setCurrentUser(userData);

      if (userData.role === "Company Admin") {
        fetchAllLocations(true);
      } else {
        fetchFilteredLocations(userData.role);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to fetch user data.");
    }
  };

  const fetchStockValuationForCard = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_BASE_URL}/latestStock/getStockValuation`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const stockData = response.data;
      if (selectedLocation.value === "all") {
        let totalValuation = locations.reduce((total, loc) => total + (stockData[loc.label]?.total_stock_value || 0), 0);
        setDashboardData((prev) => ({ ...prev, stockValuation: totalValuation }));
      } else {
        setDashboardData((prev) => ({ ...prev, stockValuation: stockData[selectedLocation.label]?.total_stock_value || 0 }));
      }
    } catch (error) {
      console.error("Error fetching stock valuation:", error);
    }
  };

  const fetchStockValuation = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_BASE_URL}/latestStock/getStockValuation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const stockData = response.data;
      let pieData = Object.keys(stockData).map((key) => ({ name: key, value: stockData[key]?.total_stock_value || 0 }));
      setStockValuationData(pieData);
    } catch (error) {
      console.error("Error fetching stock valuation:", error);
    }
  };

  const fetchAllLocations = async (isCompanyAdmin) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      let formattedLocations = response.data.locations.map(loc => ({ value: loc._id, label: loc.location_name }));
      if (isCompanyAdmin && formattedLocations.length > 1) {
        formattedLocations = [{ value: "all", label: "Select All Locations" }, ...formattedLocations];
      }
      setLocations(formattedLocations);
      setSelectedLocation(formattedLocations[0]);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Failed to fetch locations.");
    }
  };

  const fetchFilteredLocations = async (role) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      let formattedLocations = response.data.locations.map(loc => ({ value: loc._id, label: loc.location_name }));
      if ((role === "Outlet User" || role === "Farm User" || role === "Accountant") && formattedLocations.length > 1) {
        formattedLocations = [{ value: "all", label: "Select All Locations" }, ...formattedLocations];
      }
      setLocations(formattedLocations);
      setSelectedLocation(formattedLocations[0]);
    } catch (error) {
      console.error("Error fetching filtered locations:", error);
      toast.error("Failed to fetch filtered locations.");
    }
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A833FF", "#FF3366"];

  return (
    <div>
      <h2 className="text-3xl font-bold mb-6">Dashboard</h2>
      <div className="mb-4 w-1/4">
        <Select options={locations} value={selectedLocation} onChange={setSelectedLocation} placeholder="Filter by location..." isClearable />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <InfoCard
          icon={<FaDollarSign />}
          title={selectedLocation?.value === "all" ? "Total Stock Valuation" : `Stock Valuation (${selectedLocation?.label || ""})`}
          value={selectedLocation ? `LKR ${dashboardData.stockValuation.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}
          color="green"
        />
        <InfoCard
          icon={<FaFileInvoice />}
          title="Overdue Invoices"
          value={overdueInvoiceCount}
          color="red"
        />
        
      </div>

      <div className="mt-8 p-6 bg-white shadow-xl rounded-3xl w-full transform transition duration-300 ease-in-out hover:scale-105">
        <h3 className="text-xl font-semibold text-gray-700 mb-4 text-center">Total Invoice Amount</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyInvoiceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              // label={{ value: 'Month', position: 'insideBottom', offset: -5 }} 
            />
            <YAxis />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'amount') return [`LKR ${value}`, 'Invoice Amount'];
                if (name === 'settledAmount') return [`LKR ${value}`, 'Settled Amount'];
                if (name === 'balanceAmount') return [`LKR ${value}`, 'Balance Amount'];
                if (name === 'actualSettledAmount') return [`LKR ${value}`, 'Actual Settled Amount'];
                return [`LKR ${value}`, name];
              }} 
            />
            <Legend 
              formatter={(value) => {
                if (value === "amount") return "Invoice Amount (Green)";
                if (value === "balanceAmount") return "Balance Amount (Blue)";
                if (value === "settledAmount") return "Settled Amount (Red)";
                if (value === "actualSettledAmount") return "Actual Settled Amount (Orange)";
                return value;
              }}
            />
            
            <Line 
              type="monotone" 
              dataKey="balanceAmount" 
              name="Outstanding Balance" 
              stroke="#ff0000" 
              strokeWidth={2} 
              dot={{ r: 4 }} 
            />
            
            <Line
              type="monotone" 
              dataKey="actualSettledAmount" 
              name="Settled Amount" 
              stroke="#0F621D" 
              strokeWidth={2} 
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {(currentUser?.role === "Company Admin" || (locations.length > 1 && (currentUser?.role === "Outlet User" || currentUser?.role === "Farm User"))) && (
        <div className="flex  gap-6 w-full">
          {/* First Graph */}
          <div className="mt-8 p-6 bg-white shadow-xl rounded-3xl w-1/2 transform transition duration-300 ease-in-out hover:scale-105">
            <h3 className="text-xl font-semibold text-gray-700 mb-4 text-center">Stock Valuation by Location</h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={stockValuationData}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                >
                  {stockValuationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoCard = ({ icon, title, value, color }) => (
  <div className="bg-white p-6 shadow-lg rounded-lg flex items-center space-x-4 transform transition duration-300 ease-in-out hover:scale-105">
    <div className={`text-${color}-500 text-4xl`}>{icon}</div>
    <div>
      <h4 className="text-lg font-semibold text-gray-700">{title}</h4>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

export default UserDashboard1;
